import { GridColDef, GridTreeDataGroupingCell } from '@mui/x-data-grid-premium';
import { tableSettingsProps, useCompleteTableSettings, useInitialTableSettings } from '../tableSettings/useTableSettings';
import tableHelper from 'src/utils/tableHelper';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GRID_REORDER_COL_DEF } from '@mui/x-data-grid-premium';
import { useGetCategories } from '../../api/requestConfigs/salesitems';
import { CategoryResponse } from 'src/interfaces/bold/salesitem/salesitem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import AddIcon from '@mui/icons-material/Add';
import { DEFAULT_LANGUAGE_ID } from 'src/utils/apiRequests';
import Delete from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { AdditionalTableEdits, commitExternalRowUpdate } from '../tableSettings/tableEditActions';

export const UPDATE_ROOT_CATEGORIES = false;

/* -------------------------------- helpers -------------------------------- */

const catKey = (treeId: number, id: number) => `${treeId}:${id}`;
const treeKey = (treeId: number) => `tree:${treeId}`;

const isTreeGroupKey = (groupKey: string) => String(groupKey).startsWith('tree:');
const parseTreeIdFromTreeGroupKey = (groupKey: string) => Number(String(groupKey).replace('tree:', ''));

const parseCatKey = (groupKey: string): { treeId: number; categoryId: number } | null => {
    const s = String(groupKey);
    const parts = s.split(':');
    if (parts.length !== 2) return null;
    const treeId = Number(parts[0]);
    const categoryId = Number(parts[1]);
    if (Number.isNaN(treeId) || Number.isNaN(categoryId)) return null;
    return { treeId, categoryId };
};

// Only the absolute top group "tree:<id>" can be blocked.
// All other group rows (category parent nodes) are allowed to be edited.
const canEditGroupKey = (groupKey: string) => {
    if (!isTreeGroupKey(groupKey)) return true; // parent categories (and other groups) OK

    const treeId = parseTreeIdFromTreeGroupKey(groupKey);
    if (UPDATE_ROOT_CATEGORIES) return true;

    // allow for brand-new trees (negative id), block existing ones
    return treeId < 0;
};

const buildCategoryTreeMeta = (categories: CategoryResponse[]) => {
    if (!categories?.length) {
        return {
            rows: [],
            keyToLabel: new Map<string, string>(),
            rootByTree: new Map<number, CategoryResponse>(),
            keyToCategory: new Map<string, CategoryResponse>(),
        };
    }

    const byKey = new Map<string, CategoryResponse>();
    for (const c of categories) byKey.set(catKey(c.CategoryTreeId, c.CategoryId), c);

    const rootByTree = new Map<number, CategoryResponse>();
    for (const c of categories) {
        if (c.ParentId == null) rootByTree.set(c.CategoryTreeId, c);
    }

    const keyToLabel = new Map<string, string>();
    for (const [treeId, root] of rootByTree.entries()) {
        keyToLabel.set(treeKey(treeId), root?.Name ?? String(treeId));
    }
    for (const c of categories) {
        keyToLabel.set(catKey(c.CategoryTreeId, c.CategoryId), c?.Name ?? "");
    }

    const pathMemo = new Map<string, string[]>();

    const getPath = (c: CategoryResponse): string[] => {
        const k = catKey(c.CategoryTreeId, c.CategoryId);
        const cached = pathMemo.get(k);
        if (cached) return cached;

        const top = treeKey(c.CategoryTreeId);

        if (c.ParentId == null) {
            const p = [top];
            pathMemo.set(k, p);
            return p;
        }

        const parent = byKey.get(catKey(c.CategoryTreeId, c.ParentId));
        if (!parent) {
            const p = [top, k];
            pathMemo.set(k, p);
            return p;
        }

        const p = [...getPath(parent), k];
        pathMemo.set(k, p);
        return p;
    };

    const rows = categories
        .filter(category => category.ParentId != null)
        .map(category => ({
            path: getPath(category),
            Name: category?.Name,
            CategoryId: category?.CategoryId,
            ParentId: category?.ParentId,
            CategoryTreeId: category?.CategoryTreeId,
            ItemsAllowedInMultipleGroups: category?.ItemsAllowedInMultipleGroups,
        }));

    return { rows, keyToLabel, rootByTree, keyToCategory: byKey };
};

export const mapCategoriesToTree = (categories: CategoryResponse[]) => {
    return buildCategoryTreeMeta(categories).rows;
};

/* ------------------------- event guards ------------------------- */

const stopGridEvent = (e: any) => {
    e?.stopPropagation?.();
};

const stopGridKeyEvent = (e: any) => {
    e?.stopPropagation?.();
};

/* ------------------------- editor subcomponents ------------------------- */

const GroupNameEditor = ({ value, setValue, onCommit, onCancel }) => {
    return (
        <TextField
            size="small"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={onCommit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    onCommit();
                }

                if (e.key === 'Escape') {
                    e.preventDefault();
                    onCancel();
                }
            }}
            onKeyDownCapture={stopGridKeyEvent}
            onKeyUpCapture={stopGridKeyEvent}
            onMouseDown={stopGridEvent}
            onClick={stopGridEvent}
        />
    );
};

const RowNameEditor = ({ value, setValue, onCommit, onCancel }) => {
    return (
        <TextField
            size="small"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={onCommit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    onCommit();
                }

                if (e.key === 'Escape') {
                    e.preventDefault();
                    onCancel();
                }
            }}
            onKeyDownCapture={stopGridKeyEvent}
            onKeyUpCapture={stopGridKeyEvent}
            onMouseDown={stopGridEvent}
            onClick={stopGridEvent}
        />
    );
};

/* ------------------------- grouping cell component ------------------------- */

const CategoriesGroupingCell = ({
    params,
    keyToLabel,
    labelOverrides,

    editingGroupKey,
    setEditingGroupKey,
    editingGroupValue,
    setEditingGroupValue,
    commitEditingGroup,

    editingRowId,
    startEditingRow,
    editingRowValue,
    updateEditingRowValue,
    commitEditingRow,
    cancelEditingRow,

    openRootRenameBlockedDialog,
}) => {
    const node: any = params.rowNode;

    const isGroup = node?.type === 'group';
    const groupKey = isGroup ? String(node.groupingKey) : null;

    const resolvedGroupLabel = groupKey
        ? (labelOverrides.get(groupKey) ?? keyToLabel.get(groupKey) ?? groupKey)
        : '';

    const label = isGroup ? resolvedGroupLabel : (params.row?.Name ?? '');

    if (isGroup && groupKey) {
        const isEditing = editingGroupKey === groupKey;

        if (isEditing) {
            const cancel = () => setEditingGroupKey(null);

            return (
                <GroupNameEditor
                    value={editingGroupValue}
                    setValue={setEditingGroupValue}
                    onCommit={commitEditingGroup}
                    onCancel={cancel}
                />
            );
        }

        return (
            <div
                style={{ width: '100%' }}
                onDoubleClick={(e) => {
                    stopGridEvent(e);

                    // Only block the absolute top "tree:<id>" node (existing trees)
                    if (isTreeGroupKey(groupKey) && !canEditGroupKey(groupKey)) {
                        openRootRenameBlockedDialog(label);
                        return;
                    }

                    setEditingGroupKey(groupKey);
                    setEditingGroupValue(label);
                }}
                onMouseDown={stopGridEvent}
                onClick={stopGridEvent}
            >
                <GridTreeDataGroupingCell {...params} formattedValue={label} />
            </div>
        );
    }

    const isEditingRow = editingRowId === params.id;

    if (isEditingRow) {
        return (
            <RowNameEditor
                value={editingRowValue}
                setValue={updateEditingRowValue}
                onCommit={commitEditingRow}
                onCancel={cancelEditingRow}
            />
        );
    }

    return (
        <div
            style={{ width: '100%' }}
            onDoubleClick={(e) => {
                stopGridEvent(e);
                startEditingRow(params.id, params.row?.Name ?? '');
            }}
            onMouseDown={stopGridEvent}
            onClick={stopGridEvent}
        >
            <GridTreeDataGroupingCell {...params} formattedValue={label} />
        </div>
    );
};

/* ---------------------- grouping colDef factory ---------------------- */

const createCategoriesGroupingColDef = ({
    keyToLabel,
    labelOverrides,

    editingGroupKey,
    setEditingGroupKey,
    editingGroupValue,
    setEditingGroupValue,
    commitEditingGroup,

    editingRowId,
    startEditingRow,
    editingRowValue,
    updateEditingRowValue,
    commitEditingRow,
    cancelEditingRow,

    openRootRenameBlockedDialog,
}) => ({
    headerName: '',
    filterable: true,
    hideable: false,
    disableColumnMenu: false,
    sortable: true,
    hideDescendantCount: false,
    flex: 2,
    renderCell: (params) => (
        <CategoriesGroupingCell
            params={params}
            keyToLabel={keyToLabel}
            labelOverrides={labelOverrides}
            editingGroupKey={editingGroupKey}
            setEditingGroupKey={setEditingGroupKey}
            editingGroupValue={editingGroupValue}
            setEditingGroupValue={setEditingGroupValue}
            commitEditingGroup={commitEditingGroup}
            editingRowId={editingRowId}
            startEditingRow={startEditingRow}
            editingRowValue={editingRowValue}
            updateEditingRowValue={updateEditingRowValue}
            commitEditingRow={commitEditingRow}
            cancelEditingRow={cancelEditingRow}
            openRootRenameBlockedDialog={openRootRenameBlockedDialog}
        />
    ),
});

export interface CategoriesExtraEdits extends AdditionalTableEdits {
    additionalEditsRef: React.MutableRefObject<Map<string, any>>;
    hasAdditionalEdits: boolean;
    clearAdditionalEdits: () => void;
    commitEditingGroup: () => void;
}

/* --------------------------- main hook --------------------------- */

export const useCategoriesTable = (
    dependency = null,
    inputLanguage = DEFAULT_LANGUAGE_ID
): [GridColDef[], tableSettingsProps, CategoriesExtraEdits, React.ReactNode] => {

    const categories: CategoryResponse[] = useGetCategories(inputLanguage, dependency);
    const { rows, keyToLabel, rootByTree, keyToCategory } = useMemo(() => buildCategoryTreeMeta(categories), [categories]);

    const additionalEditsRef = useRef<Map<string, any>>(new Map());

    const [labelOverrides, setLabelOverrides] = useState<Map<string, string>>(new Map());
    const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
    const [editingGroupValue, setEditingGroupValue] = useState<string>('');

    const [editingRowId, setEditingRowId] = useState<any>(null);
    const [editingRowValue, setEditingRowValue] = useState<string>('');

    const editingRowIdRef = useRef<any>(null);
    const editingRowValueRef = useRef<string>('');

    // used only to trigger rerender when additionalEditsRef changes
    const [additionalEditsVersion, setAdditionalEditsVersion] = useState(0);

    // Root rename blocked dialog state
    const [rootRenameBlockedOpen, setRootRenameBlockedOpen] = useState(false);
    const [blockedRootLabel, setBlockedRootLabel] = useState<string>('');

    const openRootRenameBlockedDialog = (label?: string) => {
        setBlockedRootLabel(label ?? '');
        setRootRenameBlockedOpen(true);
    };

    const closeRootRenameBlockedDialog = () => {
        setRootRenameBlockedOpen(false);
    };

    const getGroupLabel = (groupKey: string) => {
        return labelOverrides.get(groupKey) ?? keyToLabel.get(groupKey) ?? groupKey;
    };

    const startEditingRow = (id: any, value: string) => {
        editingRowIdRef.current = id;
        editingRowValueRef.current = value ?? '';

        setEditingRowId(id);
        setEditingRowValue(value ?? '');
    };

    const updateEditingRowValue = (value: string) => {
        editingRowValueRef.current = value;
        setEditingRowValue(value);
    };

    const cancelEditingRow = () => {
        editingRowIdRef.current = null;
        editingRowValueRef.current = '';

        setEditingRowId(null);
        setEditingRowValue('');
    };

    const commitEditingGroup = () => {
        if (!editingGroupKey) return;

        const groupKey = editingGroupKey;
        const value = editingGroupValue;

        // Case A: absolute top node for the tree: "tree:<CategoryTreeId>"
        if (isTreeGroupKey(groupKey)) {
            if (!canEditGroupKey(groupKey)) {
                const label = getGroupLabel(groupKey);
                setEditingGroupKey(null);
                setEditingGroupValue('');
                openRootRenameBlockedDialog(label);
                return;
            }

            const treeId = parseTreeIdFromTreeGroupKey(groupKey);
            const root = rootByTree.get(treeId);

            if (!root) {
                additionalEditsRef.current.set(groupKey, { groupingKey: groupKey, Name: value });
            } else {
                additionalEditsRef.current.set(groupKey, {
                    CategoryId: root.CategoryId,
                    ParentId: null,
                    CategoryTreeId: root.CategoryTreeId,
                    Name: value,
                });
            }

            setLabelOverrides(prev => {
                const next = new Map(prev);
                next.set(groupKey, value);
                return next;
            });

            setEditingGroupKey(null);
            setAdditionalEditsVersion(v => v + 1);
            return;
        }

        // Case B: parent-category group node: "<treeId>:<categoryId>"
        const parsed = parseCatKey(groupKey);
        if (!parsed) {
            // fallback: just update label in UI
            setLabelOverrides(prev => {
                const next = new Map(prev);
                next.set(groupKey, value);
                return next;
            });
            setEditingGroupKey(null);
            setAdditionalEditsVersion(v => v + 1);
            return;
        }

        const existing = keyToCategory.get(groupKey);

        // Store an edit shaped like a normal category row (for update payload)
        if (existing) {
            additionalEditsRef.current.set(groupKey, {
                CategoryId: existing.CategoryId,
                ParentId: existing.ParentId,
                CategoryTreeId: existing.CategoryTreeId,
                Name: value,
            });
        } else {
            // For new categories that became parents, we may not have them in keyToCategory yet.
            // Store best-effort shape; create mapper should handle negatives anyway.
            additionalEditsRef.current.set(groupKey, {
                CategoryId: parsed.categoryId,
                ParentId: undefined,
                CategoryTreeId: parsed.treeId,
                Name: value,
            });
        }

        setLabelOverrides(prev => {
            const next = new Map(prev);
            next.set(groupKey, value);
            return next;
        });

        setEditingGroupKey(null);
        setAdditionalEditsVersion(v => v + 1);
    };

    const clearAdditionalEdits = () => {
        additionalEditsRef.current.clear();
        setLabelOverrides(new Map());
        setEditingGroupKey(null);
        setEditingGroupValue('');
        cancelEditingRow();
        setAdditionalEditsVersion(v => v + 1);
    };

    const tableName = 'CategoriesTable';
    const initialTableSettings = useInitialTableSettings(
        {
            idPath: ['CategoryId'],
            tableName,
            treeData: true,
        },
        rows
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                getActions: (params, index) => [
                    tableHelper.DiscardChangesButton(initialTableSettings, params, index),
                ],
                type: 'actions',
                headerName: '',
                width: initialTableSettings?.hasUnsavedRows ? 80 : 1,
            },
            { ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF },
            { field: 'CategoryId', type: 'number', width: 150, renderCell: tableHelper.idCell },
            { field: 'Name', headerName: 'New Name', flex: 1, editable: true, minWidth: 200 },
            {
                field: 'Edit',
                headerName: 'Edit',
                minWidth: 170,
                renderCell: (params) => (
                    <>
                        <CustomButton
                            iconButton
                            icon={<AddIcon />}
                            onClick={() => {
                                const categoryId = -Math.floor(Math.random() * 1000000);
                                const newKey = catKey(params.row.CategoryTreeId, categoryId);
                                initialTableSettings.addRow({
                                    path: [...params.row.path, newKey],
                                    Name: 'NEW CATEGORY',
                                    CategoryId: categoryId,
                                    ParentId: params.row.CategoryId,
                                    CategoryTreeId: params.row.CategoryTreeId,
                                });
                            }}
                        />
                        <CustomButton
                            title="Delete"
                            color="error"
                            size="small"
                            iconButton
                            icon={<Delete />}
                            onClick={() => initialTableSettings.deleteRow(params.id)}
                        />
                    </>
                ),
            },
            { ...GRID_REORDER_COL_DEF },
            { field: 'ParentId', headerName: 'Parent Id', renderCell: tableHelper.idCell, width: 100 },
            { field: 'ItemsAllowedInMultipleGroups', headerName: 'Allow multuple items', type: 'boolean' },
            { field: 'CategoryTreeId', headerName: 'Category Tree Id', renderCell: tableHelper.idCell, width: 100 },
        ],
        [initialTableSettings]
    );

    const tableSettings = useCompleteTableSettings(
        { columns, hasToggleMenu: true },
        initialTableSettings
    );

    const commitEditingRow = () => {
        const id = editingRowIdRef.current;
        if (id == null) return;

        const value = editingRowValueRef.current;

        // Clear edit state synchronously first to avoid double commit from Enter + blur.
        editingRowIdRef.current = null;
        editingRowValueRef.current = '';

        setEditingRowId(null);
        setEditingRowValue('');

        const originalRow =
            tableSettings?.apiRef?.current?.getRow?.(id) ??
            rows.find((row: any) => row.CategoryId === id);

        if (!originalRow) return;

        if (Object.is(originalRow.Name, value)) return;

        const updatedRow = {
            ...originalRow,
            Name: value,
        };

        commitExternalRowUpdate(tableSettings, updatedRow, originalRow);
    };

    tableSettings.groupingColDef = createCategoriesGroupingColDef({
        keyToLabel,
        labelOverrides,
        editingGroupKey,
        setEditingGroupKey,
        editingGroupValue,
        setEditingGroupValue,
        commitEditingGroup,

        editingRowId,
        startEditingRow,
        editingRowValue,
        updateEditingRowValue,
        commitEditingRow,
        cancelEditingRow,

        openRootRenameBlockedDialog,
    });

    tableSettings.hasToolbar = true;

    useEffect(() => {
        tableSettings.setColumnVisibilityModel({ Name: false });
    }, []);

    const hasAdditionalEdits = additionalEditsRef.current.size > 0 && additionalEditsVersion >= 0;

    const pendingOriginalRow =
        editingRowId != null
            ? (
                tableSettings?.apiRef?.current?.getRow?.(editingRowId) ??
                rows.find((row: any) => row.CategoryId === editingRowId)
            )
            : null;

    const hasPendingRowEdit =
        editingRowId != null &&
        (
            !pendingOriginalRow ||
            !Object.is(pendingOriginalRow.Name ?? '', editingRowValue ?? '')
        );

    const categoriesEdits: CategoriesExtraEdits = {
        additionalEditsRef,
        hasAdditionalEdits,
        clearAdditionalEdits,
        commitEditingGroup,

        hasChanges: hasAdditionalEdits || hasPendingRowEdit,
        clearChanges: clearAdditionalEdits,
        commitPendingEdits: () => {
            commitEditingGroup();
            commitEditingRow();
        },
        getChanges: () => Array.from(additionalEditsRef.current.values()),
    };

    const rootRenameBlockedDialog = (
        <Dialog open={rootRenameBlockedOpen} onClose={closeRootRenameBlockedDialog}>
            <DialogTitle>Root category can’t be renamed</DialogTitle>
            <DialogContent>
                <div style={{ marginTop: 8 }}>
                    {blockedRootLabel ? (
                        <>The root category <b>{blockedRootLabel}</b> can’t be renamed after it has been created.</>
                    ) : (
                        <>The root category can’t be renamed after it has been created.</>
                    )}
                </div>
                <div style={{ marginTop: 8 }}>
                    Create a new category tree instead.
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeRootRenameBlockedDialog} variant="contained">OK</Button>
            </DialogActions>
        </Dialog>
    );

    return [columns, tableSettings, categoriesEdits, rootRenameBlockedDialog];
};